import axios from 'axios';
import config from '../../../config/config.json';

// Interface for Guide data
export interface Guide {
  id: string;
  name: string;
  description: string;
  type: GuideType;
  class: CharacterClass;
  team_id: string;
  team_name: string;
  video_url: string;
  video_file?: File;
  published: boolean;
  created_at: string;
  updated_at: string;
}

export enum GuideType {
  GENERAL = 'general',
  PVP = 'pvp',
  PVM = 'pvm',
}

export enum CharacterClass {
  ANY = 'any',
  BARBARIAN = 'barbarian',
  SORCERESS = 'sorceress',
  AMAZON = 'amazon',
  ASSASSIN = 'assassin',
  NECROMANCER = 'necromancer',
  PALADIN = 'paladin',
  DRUID = 'druid',
}

export interface Discord {
  id: string;
  name: string;
  invite_url: string;
  created_at: string;
}

// Verify authorization token exists
const verifyToken = () => {
  const token = localStorage.getItem('session_token');
  if (!token) {
    throw new Error('Authentication required');
  }
};

// Get headers with session token
export const getHeaders = () => {
  const sessionToken = localStorage.getItem('session_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (sessionToken) {
    headers['session-token'] = sessionToken;
  }

  return headers;
};

// Get auth headers without Content-Type (for FormData)
export const getAuthHeadersOnly = () => {
  const sessionToken = localStorage.getItem('session_token');
  const headers: Record<string, string> = {};

  if (sessionToken) {
    headers['session-token'] = sessionToken;
  }

  return headers;
};

/**
 * Fetch all guides
 */
export async function getAllGuides(): Promise<Guide[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/guides`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch a specific guide by ID
 */
export async function getGuide(id: string): Promise<Guide> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/guide/${id}`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Create a new guide
 */
export async function createGuide(
  guide: Omit<Guide, 'id' | 'created_at' | 'updated_at'> | FormData
): Promise<Guide> {
  verifyToken();

  // Check if guide is FormData
  const isFormData = guide instanceof FormData;

  // If it's FormData, use it directly
  if (isFormData) {
    const response = await axios.post(`${config.backend_base_url}api/guide`, guide, {
      headers: getAuthHeadersOnly(),
    });
    return response.data;
  }

  // Otherwise, use JSON as before
  const response = await axios.post(`${config.backend_base_url}api/guide`, guide, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Update an existing guide
 */
export async function updateGuide(id: string, guide: Partial<Guide> | FormData): Promise<Guide> {
  verifyToken();

  // Check if guide is FormData
  const isFormData = guide instanceof FormData;

  // If it's FormData, use it directly with auth headers only (no Content-Type)
  if (isFormData) {
    const response = await axios.put(`${config.backend_base_url}api/guide/${id}`, guide, {
      headers: getAuthHeadersOnly(), // Don't set Content-Type for FormData
    });
    return response.data;
  }

  // Otherwise, use JSON as before with Content-Type header
  const response = await axios.put(`${config.backend_base_url}api/guide/${id}`, guide, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Delete a guide
 */
export async function deleteGuide(id: string): Promise<void> {
  verifyToken();
  await axios.delete(`${config.backend_base_url}api/guide/${id}`, {
    headers: getHeaders(),
  });
}

/**
 * Fetch all guides for a specific team
 */
export async function getTeamGuides(teamId: string): Promise<Guide[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/team/${teamId}/guides`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch guides by character class
 */
export async function getGuidesByClass(characterClass: CharacterClass): Promise<Guide[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/guides/class/${characterClass}`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch guides by type
 */
export async function getGuidesByType(guideType: GuideType): Promise<Guide[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/guides/type/${guideType}`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch all Discord teams
 */
export async function getAllDiscords(): Promise<Discord[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/teams`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch a specific Discord team by ID
 */
export async function getDiscord(id: string): Promise<Discord> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/discord/${id}`, {
    headers: getHeaders(),
  });
  return response.data;
}
